import { get } from 'lodash';
import { LayerType, type ILayerProps, type ITextLayerProps } from '@/version-types';

/**
 * 获取普通文字运行时实际应用的翻转状态。
 *
 * Shape Text 已在图片或 GL 输出中处理翻转；文字编辑态则临时抑制视觉翻转。
 *
 * @param layer - 当前文字图层。
 * @returns 当前运行时应应用的水平与垂直翻转状态。
 */
export const resolveRuntimeFlip = (layer: ILayerProps) => {
    const shouldApplyRuntimeFlip = !(layer.data as ITextLayerProps).shapeExtension && !layer.__isOnTextEditing;

    return {
        flipH: shouldApplyRuntimeFlip && Boolean(layer.flipH),
        flipV: shouldApplyRuntimeFlip && Boolean(layer.flipV),
    };
};

/**
 * 验证输入（api 入参）图层的合法性
 * @param layer - 组图层（子图层全部为文本图层）或文本图层
 * @returns
 */
export const validateInputLayer = (layer: ILayerProps | null) => {
    if (!layer) return false;
    if (layer.layerType === LayerType.TEXT_LAYER) return true;
    if (layer.layerType === LayerType.GROUP_LAYER) {
        const children = layer.children;
        if (children.length === 0) return false;
        return children.every(
            (child) => child.layerType === LayerType.TEXT_LAYER && !get(child, 'data.shapeExtension'),
        );
    }
    return false;
};

/**
 * 使用安全的方式获取图层 extraInfo
 * ATTENTION: 存在历史数据 extraInfo 为空字符串的情况，需要将 extraInfo 转换为对象
 * @param layer - 图层
 * @returns
 */
export const getLayerExtraInfo = (layer: ILayerProps) => {
    if (!layer.extraInfo) return {};
    return Object.prototype.toString.call(layer.extraInfo) === '[object Object]' ? { ...layer.extraInfo } : {};
};

/**
 * 收集目标图层及其子树中的文本图层。
 * @param layer - 待遍历的根图层。
 * @returns 文本图层列表。
 */
export const collectTextLayers = (layer: ILayerProps): ILayerProps[] => {
    const ret: ILayerProps[] = [];

    const visit = (item: ILayerProps) => {
        if (item.layerType === LayerType.TEXT_LAYER) ret.push(item);
        if (item.children && item.children.length > 0) item.children.forEach(visit);
    };
    visit(layer);

    return ret;
};
